import { Component, OnInit, OnDestroy, Input, ComponentRef, ElementRef } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { SvgIconsModule } from '@library/common/materials';

import {
   ShipmentModes,
   Milestone, MilestonesConst
} from '@library/common/models';

@Component({
   selector: 'lib-tracker-line',
   templateUrl: './tracker-line.component.html',
   styleUrls: ['./tracker-line.component.scss']
})
export class TrackerLineComponent implements OnInit, OnDestroy {

   @Input() mode: ShipmentModes[] = [ ShipmentModes[0] ];
   @Input() milestoneObj: {} = {};
   @Input() plannedMilestonesObj: {} = {};
   @Input() isDelayed = false;

   milestones: Milestone[];
   current: Milestone;

   positions: number[] = [];
   percent = 0;

   tooltipRef: OverlayRef;

   constructor(
      private overlayPositionBuilder: OverlayPositionBuilder,
      private santizer: DomSanitizer,
      private overlay: Overlay,
      private svgIcons: SvgIconsModule,
   ) { }

   ngOnInit() {
      // grab milestones by mode
      this.milestones = MilestonesConst( this.mode );

      // lazy register milestone icon
      this.milestones.map( m => this.svgIcons.register( m.icon ));

      // figure out how long out line is going to be
      this.positions = this.milestones
         .map( m => m.position )
         .filter(( value, index, self ) => self.indexOf( value ) === index );

      // default to first milestone in case we weren't passed anything
      this.current = this.milestones[0];

      // set current based on passed milestoneObj
      this.milestones.map( m => {
         if ( this.milestoneObj[ m.code] ) {
            this.current = m;
         }
      });

      // set current percentage
      this.percent = ( this.current.position * ( 100 / ( this.positions.length - 1 )));
   }

   ngOnDestroy() {
      if ( this.tooltipRef ) {
         this.tooltipRef.detach();
      }
   }

   getTooltip( position: number ): { label: string, date?: string }[] {
      if ( position > this.current.position ) { return []; }

      const milestones = this.milestones.filter( m => m.position === position );

      const withDates = milestones.filter( m => this.milestoneObj[ m.code ] )
         .map( m => ({
            label: m.label,
            date: this.milestoneObj[ m.code ]
         }));

      return withDates.length ? withDates : [ ({ label: milestones.pop().label }) ];
   }

   showTooltip( p: number, el: ElementRef ) {
      const index = this.positions.indexOf( p );
      const half = Math.floor( this.positions.length / 2 );
      const origin = index < half ? 'start' : index > half ? 'end' : 'center';

      const positionStrategy = this.overlayPositionBuilder
         .flexibleConnectedTo( el )
         .withPositions([{
           offsetY: 16,
           originX: origin,
           originY: 'bottom',
           overlayX: origin,
           overlayY: 'top',
         }]);
      this.tooltipRef = this.overlay.create({ positionStrategy });

      // create portal
      const portal = new ComponentPortal( TrackerTooltipComponent );

      const tooltipRef: ComponentRef<TrackerTooltipComponent> = this.tooltipRef.attach( portal );

      tooltipRef.instance.data = this.getTooltip( p );
   }

   last_milestone( milestoneObj: {} ): string {
      return MilestonesConst( this.mode ).slice()
         .map( m => m.code )
         .filter( m => milestoneObj[ m ] ).slice( -1 )[0];
   }

   displayMilestone(position: number): any {
      const milestones = this.milestones.filter(m => m.position === position);
      const isAfterCurrent = position > this.current.position;
      const dateObj = isAfterCurrent ? this.plannedMilestonesObj : this.milestoneObj;
      if ( !dateObj ) { return isAfterCurrent ? { label: '' } : milestones.pop(); }
      const withDates = milestones
         .filter(m => dateObj[m.code])
         .map(m => ({
            label: m.label,
            date: dateObj[m.code],
            order: m.order,
            position: m.position
         }));

      if (withDates.length === 0) {
         return isAfterCurrent ? { label: '' } : milestones.pop();
      }

      return withDates.reduce((highest, current) => 
         current.order > highest.order ? current : highest);
   }

   get transform(): SafeStyle {
      return this.santizer.bypassSecurityTrustStyle(
         'translateX(-' + this.percent + '%) translateX(' + ( 1.4 * ( this.percent / 100 )) + 'em)'
      );
   }
}

@Component({
   selector: 'lib-tracker-tooltip',
   template: `
   <div class="tooltip-container mat-elevation-z4" *ngIf="data.length">
      <ng-container *ngFor="let d of data; last as last">
         <div class="tooltip-title">
            {{d.label | uppercase}}
         </div>
         <div class="tooltip-content" *ngIf="d.date">
            {{d.date | date:'mediumDate'}}
         </div>
         <br *ngIf="!last" />
      </ng-container>
   </div>`,
   styles: [
      `.tooltip-container {
         padding: 1em;
      }`
   ]
})
export class TrackerTooltipComponent {
   @Input() data: { label: string, date?: string }[] = [];
}
